import type { DisplayEvent, LaneEvent, Zoom } from './types';
import { MS_PER_DAY, startOfDay } from './time';
import { durationDays } from './format';

// Static fallbacks used in tests / before a viewport width is known.
// The live timeline derives px/day from viewport width via computePxPerDay
// so that 3M / 6M / 1Y / 2Y semantically fit N months in the visible area.
export const PX_PER_DAY: Record<Zoom, number> = {
  // The 1W week view renders its own grid (WeekGrid) and never uses pxPerDay;
  // this entry only satisfies the exhaustive Record<Zoom, …> type.
  week: 200,
  month: 40,
  quarter: 14,
  'half-year': 7,
  year: 3.5,
  '2-year': 1.8,
};

export const MONTHS_IN_VIEWPORT: Record<Zoom, number> = {
  // Placeholder only — the 1W week view uses WeekGrid, not pxPerDay.
  week: 0.25,
  month: 1,
  quarter: 3,
  'half-year': 6,
  year: 12,
  '2-year': 24,
};

export const MIN_PX_PER_DAY = 1.5;
export const AVG_DAYS_PER_MONTH = 365.25 / 12;

// Below this viewport width a portrait phone can't show two years without the
// month columns becoming unreadably thin, so 2-year keeps the floor (and scrolls)
// there. Matches the portrait breakpoint used by the header.
export const FIT_WHOLE_SPAN_MIN_WIDTH = 640;

export function computePxPerDay(zoom: Zoom, viewportWidth: number): number {
  if (!viewportWidth || viewportWidth <= 0) return PX_PER_DAY[zoom];
  const months = MONTHS_IN_VIEWPORT[zoom];
  const raw = viewportWidth / (months * AVG_DAYS_PER_MONTH);
  // Fit the whole span across the viewport (no floor) for the wide zooms so the
  // entire year / two years are visible at once on mobile rather than being
  // floored into a horizontal scroll. 1-year always fits; 2-year fits except on
  // narrow portrait phones.
  const fitWholeSpan =
    zoom === 'year' || (zoom === '2-year' && viewportWidth > FIT_WHOLE_SPAN_MIN_WIDTH);
  if (fitWholeSpan) return raw;
  const base = Math.max(MIN_PX_PER_DAY, raw);
  return zoom === 'month' ? base + 2 : base;
}

// Until the 1W–1M gap is measured, a scrollport this wide falls back to the zoom
// nav's right edge; a narrower one (where that edge sits near the far right) to
// dead centre.
export const ANCHOR_MIN_VIEWPORT = 900;
// How far into the scrollport the anchor may ever sit, as fractions of its width.
// The lower bound keeps the marker off the very left edge if the toolbar is
// unexpectedly tight (a wide screen still puts the 1W–1M gap well past it); the
// upper bound is the historical centre, so the anchor can only ever move the
// focused date LEFT of where it used to be.
export const ANCHOR_MIN_FRAC = 0.05;
export const ANCHOR_MAX_FRAC = 0.5;

// Where the focused date (today line, temp marker, search hit) should sit inside
// the timeline scrollport, in px from its left edge.
//
// Every width parks it mid-way through the gap between the toolbar's 1W and 1M
// buttons (`weekGapMid`), so the date rests on a line the chrome already draws
// and most of the width shows the future, which is the direction a timeline is
// read. Until that gap is measured, a wide viewport uses the zoom nav's right
// edge and a narrow one dead centre; degenerate geometry keeps the centre too.
//
// `scrollportLeft` is subtracted rather than the tray width because the toolbar
// and the timeline share the same `margin-left: var(--tray-left-w)` — the
// difference is invariant to the desktop left tray, so no tray math is needed.
export function focusAnchorOffset(opts: {
  clientWidth: number;
  scrollportLeft: number;
  zoomNavRight: number;
  weekGapMid?: number;
}): number {
  const { clientWidth, scrollportLeft, zoomNavRight, weekGapMid = 0 } = opts;
  const centre = clientWidth / 2;
  const line =
    weekGapMid > 0 ? weekGapMid : clientWidth >= ANCHOR_MIN_VIEWPORT ? zoomNavRight : 0;
  if (line <= 0) return centre;
  const raw = line - scrollportLeft;
  if (!Number.isFinite(raw)) return centre;
  return Math.min(Math.max(raw, clientWidth * ANCHOR_MIN_FRAC), clientWidth * ANCHOR_MAX_FRAC);
}

export const MIN_PILL_PX = 80;
export const MIN_VISUAL_PILL_PX = 8;
// Hairline gap kept between pills that share a lane, so back-to-back boxes
// read as separate events instead of one fused bar.
export const SAME_LANE_GAP_PX = 2;
export const LANE_HEIGHT = 32;
export const ROW_PADDING_PX = 6;

// A conservative average glyph advance (in em) used to estimate a title's
// rendered width without measuring the DOM. Shared with EventPill's label-fit
// heuristic so the two agree. BUTTON_PADDING_PX matches the pill button's 8px
// horizontal padding on each side.
export const AVG_CHAR_EM = 0.55;
export const BUTTON_PADDING_PX = 16;

// The fixed-pixel collision floors (MIN_PILL_PX and the label reservation)
// assume a desktop-ish px/day. On a compressed timeline (narrow viewport or a
// wide zoom) they span many days and force separated events into extra lanes,
// inflating row height. Scale them with px/day so the reserved footprint tracks
// the room a day actually has. Ref ~ month zoom on desktop -> scale 1 there
// (unchanged); MIN_SCALE keeps a floor so labels still get some separation.
export const COLLISION_FLOOR_REF_PX_PER_DAY = 40;
export const COLLISION_FLOOR_MIN_SCALE = 0.3;

/** Pixel offset of epoch-ms `ms` from `epoch` — left edge of the day, not centre. */
export function msToPx(ms: number, epoch: Date, pxPerDay: number): number {
  return ((ms - epoch.getTime()) / MS_PER_DAY) * pxPerDay;
}

/** Pixel offset of `date` from `epoch` — left edge of the day, not centre. */
export function dateToPx(date: Date, epoch: Date, pxPerDay: number): number {
  return msToPx(date.getTime(), epoch, pxPerDay);
}

export function pxToDate(px: number, epoch: Date, pxPerDay: number): Date {
  return new Date(epoch.getTime() + (px / pxPerDay) * MS_PER_DAY);
}

export const MID_COLUMN_MIN_PX_PER_DAY = 30;

export function assignLanes(
  events: DisplayEvent[],
  pxPerDay: number,
  epoch: Date,
  collisionMinPx: number = MIN_PILL_PX,
  presorted = false,
  // Root em size (px) the pill title renders at, used to reserve a title's
  // estimated label width during collision so long labels push neighbours to a
  // new lane instead of visibly overlapping. 0 disables the reservation (keeps
  // the pre-existing packing for tests / callers that don't pass it).
  fontEmPx = 0,
  // When set, current/future events (end >= nowMs) claim the top lanes and past
  // events yield to them: a past event only drops to a lower lane where it would
  // actually overlap a current/future pill, otherwise it reuses the top lanes
  // (so rows stay as compact as the flat packing). Omit for flat greedy packing.
  nowMs?: number,
  // Right edge of the timeline canvas (px), i.e. `totalWidth` = dateToPx(rangeEnd).
  // When set, each rendered pill is clamped to [0, rightEdgePx] so an event that
  // straddles a window boundary ends exactly at the edge instead of overflowing
  // into the trailing pad. Omit to leave geometry unclamped (tests / callers that
  // don't know the canvas width).
  rightEdgePx?: number,
): { laneEvents: LaneEvent[]; laneCount: number; forwardLaneCount: number } {
  if (events.length === 0) return { laneEvents: [], laneCount: 0, forwardLaneCount: 0 };
  // Sort order is independent of pxPerDay, so callers re-running this on every
  // zoom can pre-sort once and pass presorted to skip the per-frame sort.
  const sorted = presorted ? events : [...events].sort((a, b) => a.start.getTime() - b.start.getTime());
  const laneEvents: LaneEvent[] = [];
  const useFractional = pxPerDay >= MID_COLUMN_MIN_PX_PER_DAY;
  // Depends only on pxPerDay, so compute it once for the whole pass. Shrinks the
  // fixed-pixel collision floors as the timeline compresses (see the constants).
  const floorScale = Math.min(
    1,
    Math.max(COLLISION_FLOOR_MIN_SCALE, pxPerDay / COLLISION_FLOOR_REF_PX_PER_DAY),
  );

  // Hidden-style pills render as barely-visible ghosts, so overlapping one is
  // acceptable: they take the lane first-fit finds but never reserve space in
  // it — they can't push a real event to a new lane (and are skipped by the
  // same-lane clip pass below).
  const isGhost = (event: DisplayEvent): boolean => event.styleVariant === 'hidden';

  // Same-title lane affinity: instances of a recurring event (same trimmed
  // displayTitle) prefer the lane the title was first placed on, so a weekly
  // meeting reads as one row instead of being scattered by first-fit. The lane
  // is remembered from the first placement only — a blocked instance falls back
  // to first-fit for that one event without dragging the row elsewhere.
  const titleLanes = new Map<string, number>();
  const affinityLane = (event: DisplayEvent): number | undefined =>
    titleLanes.get(event.displayTitle.trim());
  const rememberLane = (event: DisplayEvent, lane: number): void => {
    const title = event.displayTitle.trim();
    if (title && !titleLanes.has(title)) titleLanes.set(title, lane);
  };

  // The horizontal footprint of a pill: `left`..`left + collisionWidth` is the
  // span used for stacking, `visualWidth` is the rendered width.
  const geom = (event: DisplayEvent): { left: number; right: number; visualWidth: number } => {
    const fractional = useFractional && !event.allDay;
    const left = fractional
      ? dateToPx(event.start, epoch, pxPerDay)
      : dateToPx(startOfDay(event.start), epoch, pxPerDay);
    const days = durationDays(event.start, event.end);
    const allowMinClamp = pxPerDay >= MID_COLUMN_MIN_PX_PER_DAY;
    const visualWidth = allowMinClamp
      ? Math.max(days * pxPerDay, MIN_VISUAL_PILL_PX)
      : days * pxPerDay;
    const realDurationPx = fractional
      ? ((event.end.getTime() - event.start.getTime()) / MS_PER_DAY) * pxPerDay
      : visualWidth;
    // Estimated on-screen label footprint. Only reserved on the non-fractional
    // path (all-day events and every event at wider zooms) — where thin pills
    // let long titles smear over neighbours; fractional timed events keep their
    // real-duration collision so concurrent meetings aren't force-stacked.
    const labelPx =
      fontEmPx > 0
        ? event.displayTitle.trim().length * AVG_CHAR_EM * fontEmPx + BUTTON_PADDING_PX
        : 0;
    // Fractional pills render at least MIN_VISUAL_PILL_PX wide (plus the
    // same-lane gap), so collision must reserve at least that much or two
    // short meetings minutes apart would share a lane with physically
    // overlapping boxes.
    const collisionWidth = fractional
      ? Math.max(realDurationPx, MIN_VISUAL_PILL_PX + SAME_LANE_GAP_PX)
      : Math.max(visualWidth, collisionMinPx * floorScale, labelPx * floorScale);
    return { left, right: left + collisionWidth, visualWidth };
  };

  // Rendered widths are day-rounded (durationDays floors to a whole day), so a
  // timed pill's box can reach past its collision footprint — e.g. an evening
  // meeting renders into the next day. Clip each pill at its same-lane
  // successor so boxes never physically overlap, and record how much room the
  // label has before it would smear over that neighbour (labelRoomPx —
  // EventPill fades the title at that edge).
  const clipToLaneNeighbours = (): void => {
    const byLane = new Map<number, LaneEvent[]>();
    for (const e of laneEvents) {
      if (isGhost(e)) continue; // ghosts neither clip nor get clipped
      const group = byLane.get(e.lane);
      if (group) group.push(e);
      else byLane.set(e.lane, [e]);
    }
    for (const group of byLane.values()) {
      group.sort((a, b) => a.leftPx - b.leftPx);
      for (let i = 0; i < group.length - 1; i++) {
        const e = group[i];
        const nextLeft = group[i + 1].leftPx;
        if (e.leftPx + e.widthPx > nextLeft) {
          e.widthPx = Math.max(MIN_VISUAL_PILL_PX, nextLeft - e.leftPx - SAME_LANE_GAP_PX);
        }
        e.labelRoomPx = nextLeft - e.leftPx;
      }
    }
  };

  // Clamp each rendered pill to the timeline canvas [0, rightEdgePx] so an event
  // that overhangs a window boundary (start before rangeStart, or end past
  // rangeEnd) stops at the edge rather than drawing into the trailing pad. No-op
  // when rightEdgePx is omitted. Left-clamp trims the width by the amount cut so
  // the pill's right edge stays put.
  const clampToCanvas = (): void => {
    if (rightEdgePx === undefined) return;
    for (const e of laneEvents) {
      if (e.leftPx < 0) {
        e.widthPx += e.leftPx;
        e.leftPx = 0;
      }
      const overflow = e.leftPx + e.widthPx - rightEdgePx;
      if (overflow > 0) e.widthPx -= overflow;
      if (e.widthPx < 0) e.widthPx = 0;
    }
  };

  if (nowMs === undefined) {
    // Flat greedy first-fit. Events arrive start-ascending, so a lane is free
    // once its last pill's right edge is at or before this pill's left edge.
    const laneEnds: number[] = [];
    for (const event of sorted) {
      const { left, right, visualWidth } = geom(event);
      const preferred = affinityLane(event);
      let lane =
        preferred !== undefined && laneEnds[preferred]! <= left
          ? preferred
          : laneEnds.findIndex((end) => end <= left);
      if (lane === -1) {
        // No free lane: a ghost overlaps whatever is on the top lane rather
        // than opening a new one; real events open the next lane as before.
        if (isGhost(event) && laneEnds.length > 0) lane = 0;
        else {
          lane = laneEnds.length;
          laneEnds.push(0);
        }
      }
      if (!isGhost(event)) laneEnds[lane] = right;
      rememberLane(event, lane);
      laneEvents.push({ ...event, lane, leftPx: left, widthPx: visualWidth });
    }
    clipToLaneNeighbours();
    clampToCanvas();
    return { laneEvents, laneCount: laneEnds.length, forwardLaneCount: laneEnds.length };
  }

  // Keep current/future events on the top row(s) without inflating height:
  // place them first (they claim the lowest lanes), then place past events on
  // the lowest lane where they don't actually overlap anything. Past events
  // therefore reuse the top lanes wherever there's a gap (staying compact) and
  // only drop to a lower lane when they'd collide with a current/future pill.
  const laneSpans: { left: number; right: number }[][] = [];
  // Forward (current/future) events arrive start-ascending, so within a lane the
  // last real span always has the largest right edge — a scalar "max right seen"
  // decides whether a lane has room without scanning its whole span list (the
  // flat path's trick). Past events are placed afterwards and can slot *before*
  // existing spans, so they still need the interval list. -Infinity marks a lane
  // with no real span yet (including a ghost-only lane), so it always fits.
  const laneMaxRight: number[] = [];

  // Current/future pass: O(lanes) per event via the scalar max-right.
  const placeForward = (event: DisplayEvent): void => {
    const { left, right, visualWidth } = geom(event);
    const preferred = affinityLane(event);
    let lane =
      preferred !== undefined && laneMaxRight[preferred]! <= left
        ? preferred
        : laneMaxRight.findIndex((mr) => mr <= left);
    if (lane === -1) {
      // No free lane: ghosts overlap the top lane instead of opening one.
      if (isGhost(event) && laneMaxRight.length > 0) lane = 0;
      else {
        lane = laneMaxRight.length;
        laneMaxRight.push(-Infinity);
        laneSpans.push([]);
      }
    }
    if (!isGhost(event)) {
      laneSpans[lane].push({ left, right });
      laneMaxRight[lane] = right; // monotonic: right > left >= old max-right
    }
    rememberLane(event, lane);
    laneEvents.push({ ...event, lane, leftPx: left, widthPx: visualWidth });
  };

  // Past pass: past events are also start-ascending, so any span ending at or
  // before this event's left is dead for it and every later past event — drop it
  // while scanning, so the overlap check stays bounded by concurrency instead of
  // total history. Pruned spans never overlap (their right <= left), so the fit
  // result is unchanged; this only removes them from future scans.
  const placePast = (event: DisplayEvent): void => {
    const { left, right, visualWidth } = geom(event);
    const fits = (lane: number): boolean => {
      const spans = laneSpans[lane]!;
      let w = 0;
      for (let r = 0; r < spans.length; r++) {
        const s = spans[r]!;
        if (s.right <= left) continue; // dead — drop
        spans[w++] = s;
      }
      spans.length = w;
      return !spans.some((s) => s.left < right && left < s.right);
    };
    const preferred = affinityLane(event);
    let lane =
      preferred !== undefined && fits(preferred)
        ? preferred
        : laneSpans.findIndex((_, i) => fits(i));
    if (lane === -1) {
      if (isGhost(event) && laneSpans.length > 0) lane = 0;
      else {
        lane = laneSpans.length;
        laneSpans.push([]);
        laneMaxRight.push(-Infinity); // keep the two arrays index-aligned
      }
    }
    if (!isGhost(event)) laneSpans[lane].push({ left, right });
    rememberLane(event, lane);
    laneEvents.push({ ...event, lane, leftPx: left, widthPx: visualWidth });
  };

  const past: DisplayEvent[] = [];
  for (const event of sorted) {
    if (event.end.getTime() < nowMs) past.push(event);
    else placeForward(event);
  }
  // The lanes current/future events need on their own — what a row is sized to
  // (past events only add lanes below these where they're actually in view).
  const forwardLaneCount = laneSpans.length;
  for (const event of past) placePast(event);
  clipToLaneNeighbours();
  clampToCanvas();
  return { laneEvents, laneCount: laneSpans.length, forwardLaneCount };
}

// A past location band splits where its pills leave a gap wider than this, so
// each piece carries its own label and lanes (see assignLocationLanes).
export const PAST_BAND_SPLIT_GAP_PX = 320;

// One location's slice of a row laid out by location (`subLanes: 'location'`).
export type LocationBand = {
  // The location as first spelled in the feed (after rules); '' = no location.
  label: string;
  // First lane of the band in the row, and how many lanes it spans.
  startLane: number;
  lanes: number;
  // Below the row's standing lanes: past events, only drawn while the view is
  // scrolled into the past. Past bands at different times can share lanes.
  past: boolean;
  // Horizontal extent of the band's pills (px), for showing a past band's label
  // only while its pills are in view.
  leftPx: number;
  rightPx: number;
};

/** Grouping key for a displayed location: trimmed, spaces collapsed, case-folded. */
export function locationKey(location: string): string {
  return location.trim().replace(/\s+/g, ' ').toLowerCase();
}

// assignLanes, split into one band of lanes per location. Locations with
// current/future events get standing bands — the unlabelled no-location band
// first, then by whose next event comes soonest — so the row keeps the "never
// grows into the future" rule. Past events that don't fit their location's
// standing lanes, and locations with only past events, get bands below those,
// which the row only shows while viewing the past; those are stacked most
// recent first, and bands whose pills don't overlap in time share lanes, so
// scrolling far back doesn't open a row of empty bands. A row with one
// location lays out exactly as assignLanes would.
export function assignLocationLanes(
  events: DisplayEvent[],
  pxPerDay: number,
  epoch: Date,
  collisionMinPx: number = MIN_PILL_PX,
  presorted = false,
  fontEmPx = 0,
  nowMs?: number,
  rightEdgePx?: number,
): { laneEvents: LaneEvent[]; laneCount: number; forwardLaneCount: number; bands: LocationBand[] } {
  if (events.length === 0) return { laneEvents: [], laneCount: 0, forwardLaneCount: 0, bands: [] };
  const sorted = presorted ? events : [...events].sort((a, b) => a.start.getTime() - b.start.getTime());
  // Current/future vs past: the same test assignLanes splits its passes on.
  const isForward = (e: DisplayEvent): boolean => nowMs === undefined || e.end.getTime() >= nowMs;
  type Group = { key: string; label: string; events: DisplayEvent[]; nextMs: number };
  const groups = new Map<string, Group>();
  // Hidden-style pills never reserve lane space (see assignLanes), so they
  // mustn't open a band either: they join their location's band if it has
  // real events, else the top band.
  const ghosts: DisplayEvent[] = [];
  for (const ev of sorted) {
    if (ev.styleVariant === 'hidden') {
      ghosts.push(ev);
      continue;
    }
    const key = locationKey(ev.displayLocation);
    let g = groups.get(key);
    if (!g) {
      g = { key, label: key ? ev.displayLocation.trim() : '', events: [], nextMs: Infinity };
      groups.set(key, g);
    }
    g.events.push(ev);
    if (isForward(ev) && g.nextMs === Infinity) {
      g.nextMs = ev.start.getTime();
    }
  }

  const byLabel = (a: { label: string }, b: { label: string }): number => a.label.localeCompare(b.label);
  const lastStart = (g: Group): number => g.events[g.events.length - 1]!.start.getTime();
  const forward = [...groups.values()]
    .filter((g) => g.nextMs !== Infinity)
    .sort((a, b) => (a.key === '' ? -1 : b.key === '' ? 1 : a.nextMs - b.nextMs || byLabel(a, b)));
  const pastOnly = [...groups.values()]
    .filter((g) => g.nextMs === Infinity)
    .sort((a, b) => lastStart(b) - lastStart(a) || byLabel(a, b));

  if (ghosts.length > 0) {
    // A current/future ghost must land in a standing band, or the row would
    // only draw it while viewing the past: its own location's if that one
    // stands, else the top standing band (an unlabelled one if none stands).
    // A past ghost takes its own location's band, else the top band.
    if (forward.length === 0 && ghosts.some(isForward)) {
      forward.push({ key: '', label: '', events: [], nextMs: Infinity });
    }
    if (forward.length === 0 && pastOnly.length === 0) {
      pastOnly.push({ key: '', label: '', events: [], nextMs: Infinity });
    }
    const top = forward[0] ?? pastOnly[0]!;
    const touched = new Set<Group>();
    for (const ev of ghosts) {
      const own = groups.get(locationKey(ev.displayLocation));
      const g = own && (!isForward(ev) || own.nextMs !== Infinity) ? own : top;
      g.events.push(ev);
      touched.add(g);
    }
    for (const g of touched) g.events.sort((a, b) => a.start.getTime() - b.start.getTime());
  }

  const laneEvents: LaneEvent[] = [];
  const bands: LocationBand[] = [];
  const pack = (g: Group) =>
    assignLanes(g.events, pxPerDay, epoch, collisionMinPx, true, fontEmPx, nowMs, rightEdgePx);
  const extent = (evs: LaneEvent[]): { leftPx: number; rightPx: number } => {
    let leftPx = Infinity;
    let rightPx = -Infinity;
    for (const e of evs) {
      leftPx = Math.min(leftPx, e.leftPx);
      rightPx = Math.max(rightPx, e.leftPx + e.widthPx);
    }
    return { leftPx, rightPx };
  };

  // Lanes that only past events need, per location, placed below the standing bands.
  type Chunk = { label: string; events: LaneEvent[] };
  const chunks: Chunk[] = [];
  let offset = 0;
  for (const g of forward) {
    const r = pack(g);
    const standing = r.forwardLaneCount;
    const kept: LaneEvent[] = [];
    const overflow: LaneEvent[] = [];
    // assignLanes hands back fresh objects, so lanes are renumbered in place.
    for (const e of r.laneEvents) {
      if (e.lane < standing) {
        e.lane += offset;
        kept.push(e);
      } else {
        e.lane -= standing;
        overflow.push(e);
      }
    }
    laneEvents.push(...kept);
    bands.push({ label: g.label, startLane: offset, lanes: standing, past: false, ...extent(kept) });
    offset += standing;
    if (overflow.length > 0) chunks.push({ label: g.label, events: overflow });
  }
  const forwardLaneCount = offset;
  for (const g of pastOnly) {
    const r = pack(g);
    chunks.push({ label: g.label, events: r.laneEvents });
  }

  // Each past chunk splits where its pills leave a long gap, so each piece is
  // labelled at its own first pill and only holds its lanes over its own span.
  // A piece's right edge includes each pill's reserved collision footprint
  // (the title's estimated width, as assignLanes reserves it), so a band that
  // later shares its lanes never starts under a previous band's title.
  const floorScale = Math.min(
    1,
    Math.max(COLLISION_FLOOR_MIN_SCALE, pxPerDay / COLLISION_FLOOR_REF_PX_PER_DAY),
  );
  const reservedRight = (e: LaneEvent): number => {
    const labelPx =
      fontEmPx > 0 ? e.displayTitle.trim().length * AVG_CHAR_EM * fontEmPx + BUTTON_PADDING_PX : 0;
    return e.leftPx + Math.max(e.widthPx, collisionMinPx * floorScale, labelPx * floorScale);
  };
  type Piece = { label: string; lanes: number; events: LaneEvent[]; lastStartMs: number; leftPx: number; rightPx: number };
  const pieces: Piece[] = [];
  for (const c of chunks) {
    const byLeft = [...c.events].sort((x, y) => x.leftPx - y.leftPx);
    let cur: LaneEvent[] = [];
    let curRight = -Infinity;
    const flush = (): void => {
      if (cur.length === 0) return;
      // Renumber to the lanes this piece actually uses, keeping their order.
      const used = [...new Set(cur.map((e) => e.lane))].sort((x, y) => x - y);
      const remap = new Map(used.map((l, i) => [l, i]));
      let lastStartMs = -Infinity;
      for (const e of cur) {
        e.lane = remap.get(e.lane)!;
        lastStartMs = Math.max(lastStartMs, e.start.getTime());
      }
      pieces.push({ label: c.label, lanes: used.length, events: cur, lastStartMs, leftPx: cur[0]!.leftPx, rightPx: curRight });
      cur = [];
      curRight = -Infinity;
    };
    for (const e of byLeft) {
      if (cur.length > 0 && e.leftPx > curRight + PAST_BAND_SPLIT_GAP_PX) flush();
      cur.push(e);
      curRight = Math.max(curRight, reservedRight(e));
    }
    flush();
  }

  // Most recent first, each at the first lanes below the standing bands where
  // no band already placed overlaps it in both lanes and time.
  pieces.sort((x, y) => y.lastStartMs - x.lastStartMs || byLabel(x, y));
  let laneCount = forwardLaneCount;
  const placed: LocationBand[] = [];
  for (const c of pieces) {
    const { leftPx, rightPx } = c;
    let at = forwardLaneCount;
    for (;;) {
      const hit = placed.find(
        (p) => p.startLane < at + c.lanes && at < p.startLane + p.lanes && p.leftPx < rightPx && leftPx < p.rightPx,
      );
      if (!hit) break;
      at = hit.startLane + hit.lanes;
    }
    for (const e of c.events) {
      e.lane += at;
      laneEvents.push(e);
    }
    const band: LocationBand = { label: c.label, startLane: at, lanes: c.lanes, past: true, leftPx, rightPx };
    placed.push(band);
    bands.push(band);
    laneCount = Math.max(laneCount, at + c.lanes);
  }
  return { laneEvents, laneCount, forwardLaneCount, bands };
}

// Generic interval packer for the 1W week grid: greedily assigns each item to
// the first lane whose previous item has ended (by start/end minutes within a
// single day column), splitting overlapping events into side-by-side
// sub-columns. Items are packed in ascending-start order; `laneCount` is the
// number of side-by-side columns the day needs. Unlike assignLanes this is pure
// interval math (no pixels, no all-day clamping) so it stays trivially testable.
export type PackItem = { startMin: number; endMin: number };
export type Packed<T> = { item: T; lane: number };

export function packLanes<T extends PackItem>(
  items: T[],
): { packed: Packed<T>[]; laneCount: number } {
  if (items.length === 0) return { packed: [], laneCount: 0 };
  const sorted = [...items].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin,
  );
  const laneEnds: number[] = [];
  const packed: Packed<T>[] = [];
  for (const item of sorted) {
    // First lane free at this item's start. A zero-length item still occupies a
    // lane for its instant, and a lane is reusable the moment its prior item
    // ends (end <= start), matching back-to-back calendar events sharing a lane.
    let lane = laneEnds.findIndex((end) => end <= item.startMin);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(0);
    }
    laneEnds[lane] = item.endMin;
    packed.push({ item, lane });
  }
  return { packed, laneCount: laneEnds.length };
}

export type RangeBounds = { pastMonths?: number; futureMonths?: number };

export function rangeForToday(today: Date, bounds: RangeBounds = {}): { start: Date; end: Date } {
  const pastMonths = bounds.pastMonths ?? 12;
  const futureMonths = bounds.futureMonths ?? 24;
  const start = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - pastMonths, today.getUTCDate()),
  );
  const end = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + futureMonths, today.getUTCDate()),
  );
  return { start, end };
}

// True when an event's [start, end] overlaps the timeline window [startMs, endMs]
// (both bounds inclusive). Mirrors the parse-time overlap test in ics-core so the
// display pipeline drops events that fall entirely outside the current window —
// e.g. cached events left over from a wider window, or local-lane events that are
// never re-parsed against it. `startMs`/`endMs` are epoch millis.
export function overlapsWindow(ev: { start: Date; end: Date }, startMs: number, endMs: number): boolean {
  return ev.end.getTime() >= startMs && ev.start.getTime() <= endMs;
}

// Pixel strips for the days in `dayKeys`, over the range's days (`allDays`,
// with their precomputed `allDayKeys`). Consecutive blocked days coalesce into
// one strip: abutting hatch tiles each clip the same
// background-attachment:fixed gradient to their own sub-pixel box, doubling
// opacity at every shared edge (desktop seam / moiré). One wide strip per run
// has no internal edges to double.
export function coalesceDayStrips(
  allDays: readonly Date[],
  allDayKeys: readonly string[],
  dayKeys: ReadonlySet<string>,
  rangeStart: Date,
  pxPerDay: number,
): { left: number; width: number }[] {
  if (dayKeys.size === 0) return [];
  const out: { left: number; width: number }[] = [];
  for (let i = 0; i < allDays.length; i++) {
    if (!dayKeys.has(allDayKeys[i]!)) continue;
    const left = dateToPx(allDays[i]!, rangeStart, pxPerDay);
    const prev = out[out.length - 1];
    if (prev && Math.abs(prev.left + prev.width - left) < 0.5) {
      prev.width += pxPerDay;
    } else {
      out.push({ left, width: pxPerDay });
    }
  }
  return out;
}
